/**
 * Client-side lead dedup guard.
 *
 * Forms can double-submit when:
 *  - users tab away during the submit and hit submit again on return
 *  - browser-restored form auto-submits on history-back navigations
 *  - autofill triggers a second submit attempt
 *
 * This guard uses sessionStorage to remember a (source, email) submission for
 * a short window (default 5 minutes) and tells the form to skip re-sending.
 * Server-side `idempotencyKey` already dedups *emails*, but the underlying
 * lead row still gets inserted; this guard prevents that DB-side duplicate too.
 */

const KEY_PREFIX = "lead-submit:";
const DEFAULT_WINDOW_MS = 5 * 60 * 1000;

function key(source: string, email: string) {
  return `${KEY_PREFIX}${source}:${email.trim().toLowerCase()}`;
}

/** Returns true if we've recorded a recent submit from this (source, email). */
export function isDuplicateLeadSubmit(
  source: string,
  email: string,
  windowMs: number = DEFAULT_WINDOW_MS,
): boolean {
  if (typeof window === "undefined" || !email) return false;
  try {
    const raw = window.sessionStorage.getItem(key(source, email));
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < windowMs;
  } catch {
    return false;
  }
}

/** Record a submit timestamp so subsequent submissions in the window are skipped. */
export function markLeadSubmitted(source: string, email: string) {
  if (typeof window === "undefined" || !email) return;
  try {
    window.sessionStorage.setItem(key(source, email), String(Date.now()));
  } catch {
    /* sessionStorage unavailable — best-effort, ignore */
  }
}
